import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Mic, MicOff, Sparkles, X, Minimize2, Maximize2, Volume2, VolumeX, Send, MessageCircle, MinusSquare } from 'lucide-react';
import { useAuth } from '../../services/auth';
import { useVoiceAssistant } from '../../hooks/useVoiceAssistant';
import MessageBubble from './MessageBubble';
import Visualizations from './Visualizations';
import './VoiceAssistant.css';

const VoiceAssistant = ({ onClose }) => {
  const { token } = useAuth();
  const {
    isConnected,
    isListening,
    isSpeaking,
    messages,
    emotion,
    interimTranscript,
    isTyping,
    streamingMessage,
    sendMessage,
    startListening,
    stopListening,
    stopAllAudio,
    visualData
  } = useVoiceAssistant(token);

  const [isMinimized, setIsMinimized] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [inputText, setInputText] = useState('');
  const [voiceEnabled, setVoiceEnabled] = useState(false);
  const messagesEndRef = useRef(null);
  const [sessionId, setSessionId] = useState(null);

  // Stop voice when component unmounts or window is minimized
  useEffect(() => {
    return () => {
      // Cleanup: stop any ongoing speech when component unmounts
      stopAllAudio();
    };
  }, [stopAllAudio]);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, streamingMessage]);

  // Stop voice when minimized
  useEffect(() => {
    if (isMinimized) {
      stopAllAudio();
    }
  }, [isMinimized, stopAllAudio]);

  const handleSendText = () => {
    if (inputText.trim()) {
      sendMessage({ 
        type: 'text', 
        text: inputText,
        voice: voiceEnabled 
      });
      setInputText('');
    }
  };

  const handleMicButtonClick = () => {
    if (!voiceEnabled) {
      setVoiceEnabled(true);
    }

    if (isListening) {
      stopListening();
    } else {
      startListening();
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendText();
    }
  };

  const toggleVoice = () => {
    if (isSpeaking) {
      stopAllAudio();
    }
    setVoiceEnabled(!voiceEnabled);
  };

  const handleClose = () => {
    // Clear messages locally
    if (typeof sendMessage === 'function') {
      sendMessage({ type: 'clear_session' });
    }
    
    // Stop any ongoing audio
    if (typeof stopAllAudio === 'function') {
      stopAllAudio();
    }
    
    // Call the onClose prop to hide the component
    if (onClose) {
      onClose();
    }
  };

  if (isMinimized) {
    return (
      <motion.button
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        className="voice-assistant-floating"
        onClick={() => setIsMinimized(false)}
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.9 }}
      >
        <MessageCircle className="floating-icon" />
        {messages.length > 0 && (
          <span className="notification-badge">{messages.length}</span>
        )}
      </motion.button>
    );
  }

  return (
    <motion.div
      className={`voice-assistant-modern ${isExpanded ? 'expanded' : ''}`}
      initial={{ opacity: 0, y: 100, scale: 0.9 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: 100, scale: 0.9 }}
      transition={{ type: "spring", damping: 25, stiffness: 300 }}
    >
      {/* Modern Header */}
      <div className="va-modern-header">
        <div className="va-header-info">
          <div className="va-avatar">
            <Sparkles className="va-avatar-icon" />
          </div>
              <div className="va-title-section">
                <h3 className="va-title-text">LCA Assistant</h3>
                <div className="va-subtitle">
                  <span className={`va-status-dot ${isConnected ? 'connected' : 'connecting'}`}></span>
                  <span className="va-status-text">
                    {isConnected ? 'Assistant Ready' : 'Connecting...'}
                  </span>
                </div>
              </div>
        </div>
        
        <div className="va-header-controls">
          <button
            className={`va-control-btn ${voiceEnabled ? 'active' : ''}`}
            onClick={toggleVoice}
            title={voiceEnabled ? 'Disable Voice' : 'Enable Voice'}
          >
            {voiceEnabled ? <Volume2 size={16} /> : <VolumeX size={16} />}
          </button>
          
          {isSpeaking && (
            <button
              className="va-control-btn speaking"
              onClick={stopAllAudio}
              title="Stop Speaking"
            >
              <VolumeX size={16} />
            </button>
          )}
          
          <button
            className="va-control-btn"
            onClick={() => setIsExpanded(!isExpanded)}
            title={isExpanded ? 'Compact View' : 'Expand View'}
          >
            {isExpanded ? <Minimize2 size={16} /> : <Maximize2 size={16} />}
          </button>

          <button
            className="va-control-btn"
            onClick={() => setIsMinimized(true)}
            title="Minimize Assistant"
          >
            <MinusSquare size={16} />
          </button>
          
          <button
            className="va-control-btn close"
            onClick={handleClose}
            title="Close Assistant"
          >
            <X size={16} />
          </button>
        </div>
      </div>

      {/* Voice Visualization */}
      {(isListening || isSpeaking) && (
        <div className="va-voice-visual">
          <div className={`va-wave-container ${isListening ? 'listening' : 'speaking'}`}>
            <div className="va-wave"></div>
            <div className="va-wave"></div>
            <div className="va-wave"></div>
            <div className="va-wave"></div>
            <div className="va-wave"></div>
          </div>
          
          {isListening && interimTranscript && (
            <motion.div
              className="va-interim-display"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
            >
              <span className="interim-text">"{interimTranscript}"</span>
            </motion.div>
          )}
        </div>
      )}

      {/* Messages Container */}
      <div className="va-messages-modern">
        <AnimatePresence mode="popLayout">
          {messages.length === 0 && !isTyping && (
            <motion.div
              className="va-welcome-message"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                >
                  <Sparkles className="welcome-icon" />
                  <h4>Hi! I'm your LCA Assistant</h4>
                  <p>Your AI assistant for Life Cycle Assessment. Ask me about your scenarios, emissions data, or sustainability insights!</p>
                </motion.div>
          )}
          
          {messages.map((message) => (
            <MessageBubble key={message.id} message={message} />
          ))}
          
          {/* Enhanced Typing Indicator - Only show if no streaming message in messages */}
          {isTyping && !messages.some(m => m.streaming) && (
            <motion.div
              className="va-message-modern assistant typing"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              layout
            >
              <div className="message-avatar-modern">
                <Sparkles size={14} />
              </div>
              <div className="message-content-modern">
                {streamingMessage ? (
                  <div className="streaming-text-modern">
                    {streamingMessage}
                    <span className="typing-cursor-modern">|</span>
                  </div>
                ) : (
                  <div className="typing-indicator-modern">
                    <div className="typing-dots-modern">
                      <span></span>
                      <span></span>
                      <span></span>
                      </div>
                      <span className="typing-text">LCA Assistant is thinking...</span>
                    </div>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
        <div ref={messagesEndRef} />
      </div>

      {/* Visualizations */}
      {visualData && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          exit={{ opacity: 0, height: 0 }}
        >
          <Visualizations data={visualData} />
        </motion.div>
      )}

      {/* Modern Input Area */}
      <div className="va-input-modern">
        <div className="va-input-container">
          <textarea
            className="va-textarea"
            value={inputText}
            onChange={(e) => setInputText(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Ask about your LCA data..."
            disabled={!isConnected}
            rows={1}
            style={{
              resize: 'none',
              minHeight: '44px',
              maxHeight: '120px',
              height: 'auto'
            }}
          />
          
          <div className="va-input-actions">
            <button
              className={`va-mic-btn ${isListening ? 'listening' : ''}`}
              onClick={handleMicButtonClick}
              disabled={!isConnected}
              title={isListening ? 'Stop Listening' : 'Start Voice Input'}
            >
              {isListening ? <MicOff size={18} /> : <Mic size={18} />}
            </button>
            
            <button
              className="va-send-btn"
              onClick={handleSendText}
              disabled={!isConnected || !inputText.trim()}
              title="Send Message"
            >
              <Send size={18} />
            </button>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default VoiceAssistant;
